/**
 * UnifiedNotificationContext — Single notification queue for all notification types.
 *
 * Combines:
 * - Toast notifications (ephemeral errors, warnings, info, success)
 * - Active processing jobs (from ActiveJobsContext)
 * - Bell notifications (persisted job outcomes from Firestore)
 * - Activity messages (recording state changes)
 *
 * All notifications flow through one queue and render in NotificationCenter.
 *
 * See: docs/features/infrastructure/notification-system.md
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { useActiveJobsContext } from "./ActiveJobsContext";
import { useNotifications } from "../hooks/useNotifications";
import { notificationDebounce } from "../globals";

const UnifiedNotificationContext = createContext(null);

// Notification types
export const NOTIF_TYPE = {
  JOB: "job",           // Active processing job
  SUCCESS: "success",   // Completed action
  ERROR: "error",       // Error requiring attention
  WARNING: "warning",   // Warning (may auto-dismiss)
  INFO: "info",         // Informational
};

// Default durations (ms) - 0 means no auto-dismiss
const DEFAULT_DURATIONS = {
  [NOTIF_TYPE.JOB]: 0,        // Never auto-dismiss
  [NOTIF_TYPE.SUCCESS]: 30000, // 30 seconds
  [NOTIF_TYPE.ERROR]: 0,       // Requires manual dismiss
  [NOTIF_TYPE.WARNING]: 30000, // 30 seconds
  [NOTIF_TYPE.INFO]: 10000,    // 10 seconds
};

let notificationIdCounter = 0;

export function UnifiedNotificationProvider({ userId, children }) {
  // Local ephemeral notifications queue
  const [localNotifications, setLocalNotifications] = useState([]);
  
  // Active jobs from existing context
  const activeJobsCtx = useActiveJobsContext();
  const { activeJobs, pendingJobs, addPendingJob, removePendingJob, dismiss: dismissJob } = activeJobsCtx;

  // Firestore notifications (bell)
  const { notifications: firestoreNotifs, unreadCount, markAsRead, markAllRead } = useNotifications(userId);

  // Debounce tracking
  const lastShownRef = useRef(new Map());
  const timersRef = useRef(new Map());

  /**
   * Add a notification to the unified queue.
   * @param {string} message - Notification message
   * @param {string} type - One of NOTIF_TYPE values
   * @param {Object} options - { duration?, key?, onClick?, data?, priority? }
   *   - priority: if true, notification is shown as a floating popup toast
   *     (rendered by PriorityNotificationPopup). Defaults to true for errors.
   *     Non-priority notifications appear only in the bell dropdown tray.
   */
  const addNotification = useCallback((message, type = NOTIF_TYPE.INFO, options = {}) => {
    const { key, onClick, data, duration, priority } = options;

    // Debounce by key if provided
    if (key) {
      const now = Date.now();
      const lastTime = lastShownRef.current.get(key);
      if (lastTime && now - lastTime < notificationDebounce) {
        return null;
      }
      lastShownRef.current.set(key, now);
    }

    const id = `notif-${++notificationIdCounter}`;
    const notification = {
      id,
      message,
      type,
      createdAt: Date.now(),
      onClick,
      data,
      dismissed: false,
      // Errors are priority by default; callers can override per-notification.
      priority: priority ?? (type === NOTIF_TYPE.ERROR),
    };

    setLocalNotifications(prev => [notification, ...prev]);

    // Set auto-dismiss timer
    const autoDismiss = duration ?? DEFAULT_DURATIONS[type];
    if (autoDismiss > 0) {
      const timer = setTimeout(() => {
        dismissNotification(id);
      }, autoDismiss);
      timersRef.current.set(id, timer);
    }

    return id;
  }, []);

  /**
   * Dismiss a local notification by ID.
   */
  const dismissNotification = useCallback((id) => {
    // Clear any pending timer
    if (timersRef.current.has(id)) {
      clearTimeout(timersRef.current.get(id));
      timersRef.current.delete(id);
    }

    setLocalNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  /**
   * Dismiss all local notifications.
   */
  const dismissAllLocal = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
    setLocalNotifications([]);
  }, []);

  /**
   * Mark a Firestore notification as read.
   */
  const dismissFirestoreNotification = useCallback((id) => {
    markAsRead(id);
  }, [markAsRead]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Wire up global listener so external showNotification calls work
  useEffect(() => {
    setGlobalNotificationListener((message, type, options) => {
      addNotification(message, type, options);
    });
    return () => {
      setGlobalNotificationListener(null);
    };
  }, [addNotification]);

  // Build combined notifications list for the UI
  // Order: Active jobs → Local (ephemeral) → Firestore (persistent)
  const allJobs = [
    ...pendingJobs.map(p => ({
      ...p,
      isPending: true,
      status: "pending",
      sourceType: p.label,
      patientName: p.patientName || "",
      progressPercent: 0,
    })),
    ...activeJobs,
  ];

  const value = {
    // Combined data
    activeJobs: allJobs,
    localNotifications,
    firestoreNotifications: firestoreNotifs,
    unreadCount,

    // Actions
    addNotification,
    dismissNotification,
    dismissAllLocal,
    dismissFirestoreNotification,
    markAllFirestoreRead: markAllRead,

    // Job-specific actions (passthrough from ActiveJobsContext)
    addPendingJob,
    removePendingJob,
    dismissJob,

    // Convenience aliases
    notify: addNotification,
  };

  return (
    <UnifiedNotificationContext.Provider value={value}>
      {children}
    </UnifiedNotificationContext.Provider>
  );
}

export function useUnifiedNotifications() {
  const ctx = useContext(UnifiedNotificationContext);
  if (!ctx) {
    throw new Error("useUnifiedNotifications must be used within UnifiedNotificationProvider");
  }
  return ctx;
}

/**
 * Standalone function to show a notification (for use outside React components).
 * This pushes to a global listener that the provider subscribes to.
 */
let globalNotificationListener = null;

export function setGlobalNotificationListener(listener) {
  globalNotificationListener = listener;
}

export function showNotification(message, type = "success", duration = null, options = {}) {
  if (globalNotificationListener) {
    globalNotificationListener(message, type, { ...options, duration });
  } else {
    // Fallback: log to console if no provider is mounted
    console.warn("[UnifiedNotification] No provider mounted, notification dropped:", message);
  }
}
